export function validateAttributeChange(newAttr, originalAttr) {
    // --- Global Validations ---

    // 1. For system attributes no changes can be made.
    if (newAttr.isSystemAttribute) {
        throw new Error("System attributes cannot be modified.");
    }

    // 2. For Azure attributes of user profile, name, type, and label are disabled.
    if (newAttr.isAzureAttribute) {
        if (newAttr.attributeName !== originalAttr.attributeName) {
            throw new Error("Cannot change attribute name for Azure attribute.");
        }
        if (newAttr.attributeType !== originalAttr.attributeType) {
            throw new Error("Cannot change attribute type for Azure attribute.");
        }
        if (newAttr.attributeLabel !== originalAttr.attributeLabel) {
            throw new Error("Cannot change attribute label for Azure attribute.");
        }
        // For email Azure attribute, email is always required.
        if (newAttr.attributeType === "Email" && !newAttr.required) {
            throw new Error("Email Azure attribute must be required.");
        }
    }

    // 3. For any attribute, its attribute type cannot be updated.
    if (newAttr.attributeType !== originalAttr.attributeType) {
        throw new Error("Attribute type cannot be updated.");
    }
    // For number: its numberType cannot change.
    if (newAttr.attributeType === "Number" && newAttr.numberType !== originalAttr.numberType) {
        throw new Error("Number type cannot be updated.");
    }
    // For date time: its dateTimeType cannot change.
    if (newAttr.attributeType === "DateTime" && newAttr.dateTimeType !== originalAttr.dateTimeType) {
        throw new Error("DateTime type cannot be updated.");
    }

    // 4. For any attribute (and also for the "rest" later), if required then readOnly must be false and vice versa.
    if (newAttr.required && newAttr.readOnly) {
        throw new Error("An attribute cannot be both required and readOnly.");
    }
    
    // --- Type-Specific Validations ---
    switch (newAttr.attributeType) {
        case "String": {
            // (Text Attribute Validations)

            // a. If readOnly then required and unique must be false.
            if (newAttr.readOnly) {
                if (newAttr.required) {
                    throw new Error("ReadOnly text attribute cannot be required.");
                }
                if (newAttr.unique) {
                    throw new Error("ReadOnly text attribute cannot be unique.");
                }
            }
            // b. If unique then readOnly must be false.
            if (newAttr.unique && newAttr.readOnly) {
                throw new Error("Unique text attribute cannot be readOnly.");
            }
            // c. If required or unique then formula cannot be added.
            if ((newAttr.required || newAttr.unique) && newAttr.formula) {
                throw new Error("Formula cannot be added to a text attribute that is required or unique.");
            }
            // d. If pattern is NOT provided, then:
            //    - Minimum and maximum length (if provided) must be > 0.
            //    - If min/max are provided, defaultValue length must be between them.
            if (!newAttr.pattern) {
                if (newAttr.min && Number(newAttr.min) <= 0) {
                    throw new Error("Minimum length must be greater than 0.");
                }
                if (newAttr.max && Number(newAttr.max) <= 0) {
                    throw new Error("Maximum length must be greater than 0.");
                }
                if (newAttr.defaultValue) {
                    const len = newAttr.defaultValue.length;
                    if (newAttr.min && len < Number(newAttr.min)) {
                        throw new Error("Default value length is less than the minimum length.");
                    }
                    if (newAttr.max && len > Number(newAttr.max)) {
                        throw new Error("Default value length exceeds the maximum length.");
                    }
                }
            } else {
                // e. If pattern is provided then min and max should not be provided.
                if (newAttr.min || newAttr.max) {
                    throw new Error("Minimum and maximum lengths are disabled when pattern is provided.");
                }
                // f. Validate pattern format (must follow kendo react mask rules).
                if (!isValidKendoPattern(newAttr.pattern)) {
                    throw new Error("Pattern does not follow the kendo react format.");
                }
                // g. If a default value is provided, it must follow the pattern.
                if (newAttr.defaultValue && !validateDefaultAgainstPattern(newAttr.pattern, newAttr.defaultValue)) {
                    throw new Error("Default value does not match the pattern format.");
                }
            }
            break;
        }
        case "Number": {
            // (Number Attribute Validations)

            // a. If required then readOnly must be false (and vice versa) already checked above.
            // b. If required, formula cannot be added.
            if (newAttr.required && newAttr.formula) {
                throw new Error("Formula cannot be added to a required number attribute.");
            }
            // c. If a format is mentioned then "show on side" must be populated
            //    and must be either "Left" or "Right". Also, maximum length of format is 10.
            if (newAttr.format) {
                if (!newAttr.unitSide || (newAttr.unitSide !== "Left" && newAttr.unitSide !== "Right")) {
                    throw new Error("Unit side must be either 'Left' or 'Right' when format is provided.");
                }
                if (newAttr.format.length > 10) {
                    throw new Error("Format maximum length is 10.");
                }
            }
            // d. For integer numberType:
            if (newAttr.numberType === "integer") {
                if (newAttr.min !== "" && (!Number.isInteger(Number(newAttr.min)) || Number(newAttr.min) > 2147483647)) {
                    throw new Error("Minimum value must be an integer and not exceed 2,147,483,647.");
                }
                if (newAttr.max !== "" && (!Number.isInteger(Number(newAttr.max)) || Number(newAttr.max) > 2147483647)) {
                    throw new Error("Maximum value must be an integer and not exceed 2,147,483,647.");
                }
                if (newAttr.defaultValue !== "" && (!Number.isInteger(Number(newAttr.defaultValue)) || Number(newAttr.defaultValue) > 2147483647)) {
                    throw new Error("Default value must be an integer and not exceed 2,147,483,647.");
                }
            }
            // e. For decimal numberType:
            if (newAttr.numberType === "decimal") {
                if (newAttr.min !== "" && (isNaN(Number(newAttr.min)) || Number(newAttr.min) > 2147483647)) {
                    throw new Error("Minimum value must be a decimal and not exceed 2,147,483,647.");
                }
                if (newAttr.max !== "" && (isNaN(Number(newAttr.max)) || Number(newAttr.max) > 2147483647)) {
                    throw new Error("Maximum value must be a decimal and not exceed 2,147,483,647.");
                }
                if (newAttr.defaultValue !== "" && (isNaN(Number(newAttr.defaultValue)) || Number(newAttr.defaultValue) > 2147483647)) {
                    throw new Error("Default value must be a decimal and not exceed 2,147,483,647.");
                }
                // f. Decimal places must be provided and be one of 0, 1, 2, 3, 4, 5.
                const validDecimalPlaces = [0, 1, 2, 3, 4, 5];
                if (newAttr.decimalPlace === undefined || !validDecimalPlaces.includes(Number(newAttr.decimalPlace))) {
                    throw new Error("Decimal places must be one of 0, 1, 2, 3, 4, 5.");
                }
            }
            break;
        }
        case "Picklist String": {
            // (Picklist String Validations)

            const picklist = newAttr.picklist.split('ɖ')
            // a. Each option cannot be blank or spaces only.
            const trimmedOptions = picklist.map(opt => opt.trim());
            for (let opt of trimmedOptions) {
                if (opt === "") {
                    throw new Error("Picklist options cannot be blank or spaces only.");
                }
            }
            // b. No duplicate picklist option.
            const uniqueOptions = new Set(trimmedOptions);
            if (uniqueOptions.size !== trimmedOptions.length) {
                throw new Error("Duplicate picklist options are not allowed.");
            }
            // c. If a default value is provided, it must be one of the picklist options.
            if (newAttr.defaultValue && !trimmedOptions.includes(newAttr.defaultValue.trim())) {
                throw new Error("Default value must be one of the picklist options.");
            }
            break;
        }
        case "DateTime": {
            // (Date Time Attribute Validations)
            if (newAttr.dateTimeType === "date") {
                // For date type:
                if (newAttr.required && newAttr.readOnly) {
                    throw new Error("For date type, if required, readOnly must be false.");
                }
                if (newAttr.required && newAttr.formula) {
                    throw new Error("For date type, formula cannot be added if required.");
                }
                if (newAttr.readOnly) {
                    throw new Error("For date type, readOnly must be false.");
                }
                if (newAttr.defaultDate && !isValidDefaultDate(newAttr.defaultDate)) {
                    throw new Error("Default date format is invalid for date type.");
                }
            } else if (newAttr.dateTimeType === "datetime") {
                // For datetime type:
                if (newAttr.required) {
                    throw new Error("For datetime type, required must be false.");
                }
                if (newAttr.required && newAttr.formula) {
                    throw new Error("For datetime type, formula cannot be added if required.");
                }
                if (newAttr.readOnly) {
                    throw new Error("For datetime type, readOnly must be false.");
                }
                if (newAttr.defaultDate && !isValidDefaultDate(newAttr.defaultDate)) {
                    throw new Error("Default date format is invalid for datetime type.");
                }
                if (newAttr.defaultTime && !isValidTimeFormat(newAttr.defaultTime)) {
                    throw new Error("Default time format is invalid for datetime type.");
                }
                const validIntervals = [1, 5, 10, 15, 30];
                if (!validIntervals.includes(Number(newAttr.interval))) {
                    throw new Error("Interval must be one of 1, 5, 10, 15, 30 for datetime type.");
                }
                if (newAttr.minTime && !isValidTimeFormat(newAttr.minTime)) {
                    throw new Error("Earliest time format is invalid for datetime type.");
                }
                if (newAttr.maxTime && !isValidTimeFormat(newAttr.maxTime)) {
                    throw new Error("Latest time format is invalid for datetime type.");
                }
                // (Optional: if minTime or maxTime are defined, you might check that defaultTime is within the range.)
            } else if (newAttr.dateTimeType === "time") {
                // For time type:
                if (newAttr.required && newAttr.readOnly) {
                    throw new Error("For time type, if required, readOnly must be false.");
                }
                if (newAttr.readOnly && newAttr.required) {
                    throw new Error("For time type, readOnly cannot be true if required.");
                }
                if (newAttr.formula) {
                    throw new Error("For time type, formula cannot be added.");
                }
                if (newAttr.defaultTime && !isValidTimeFormat(newAttr.defaultTime)) {
                    throw new Error("Default time format is invalid for time type.");
                }
                const validIntervals = [1, 5, 10, 15, 30];
                if (!validIntervals.includes(Number(newAttr.interval))) {
                    throw new Error("Interval must be one of 1, 5, 10, 15, 30 for time type.");
                }
                if (newAttr.minTime && !isValidTimeFormat(newAttr.minTime)) {
                    throw new Error("Earliest time format is invalid for time type.");
                }
                if (newAttr.maxTime && !isValidTimeFormat(newAttr.maxTime)) {
                    throw new Error("Latest time format is invalid for time type.");
                }
                // (Optional: Check that defaultTime lies within minTime and maxTime if provided.)
            }
            break;
        }
        case "Email":
        case "Link": {
            // (Email / Link Validations)
            if (newAttr.defaultValue) {
                if (newAttr.attributeType === "Email" && !isValidEmail(newAttr.defaultValue)) {
                    throw new Error("Default value must be a valid email address.");
                }
                if (newAttr.attributeType === "Link" && !isValidURL(newAttr.defaultValue)) {
                    throw new Error("Default value must be a valid URL.");
                }
            }
            break;
        }
        case "Boolean": {
            // (Boolean Validations)
            if (!["True", "False"].includes(newAttr.defaultValue)) {
                throw new Error("Default value for a Boolean attribute must be either true or false.");
            }
            break;
        }
        case "State Field": {
            // (State Field Validations)
            if (!Array.isArray(newAttr.statefield)) {
                throw new Error("State field must be an array.");
            }
            const stateNames = [];
            for (let state of newAttr.statefield) {
                if (!state.name || state.name.trim() === "") {
                    throw new Error("Each state in statefield cannot be blank.");
                }
                if (stateNames.includes(state.name.trim())) {
                    throw new Error("Each state in statefield must be unique.");
                }
                stateNames.push(state.name.trim());
            }
            if (!newAttr.required) {
                throw new Error("State Field attribute is always required.");
            }
            break;
        }
        case "Image": {
            // (Image Validations)
            const allowedFileTypes = ["JPEG", "PNG", "GIF", "TIFF", "SVG"];
            if (!allowedFileTypes.includes(newAttr.fileType)) {
                throw new Error("Image file type must be one of JPEG, PNG, GIF, TIFF, SVG.");
            }
            if (newAttr.numberOfAttachments < 1 || newAttr.numberOfAttachments > 30) {
                throw new Error("Number of attachments for an image must be between 1 and 30.");
            }
            if (newAttr.maxSize < 1 || newAttr.maxSize > 10) {
                throw new Error("Maximum size for an image must be between 1 and 10.");
            }
            break;
        }
        case "Tree Dropdown":
            // --- Tree Dropdown Validation ---
            if (!newAttr.treeDropdown) {
                throw new Error("Tree Dropdown attribute must have a treeDropdown property.");
            }
            if (!Array.isArray(newAttr.treeDropdown)) {
                throw new Error("treeDropdown property must be an array.");
            }
            newAttr.treeDropdown.forEach(node => {
                validateTreeDropdownNode(node);
            });
            break;
        default: {
            // (All the rest of attributes: enforce mutual exclusion of required and readOnly)
            if (newAttr.required && newAttr.readOnly) {
                throw new Error("An attribute cannot be both required and readOnly.");
            }
            if (newAttr.readOnly && newAttr.required) {
                throw new Error("An attribute cannot be both readOnly and required.");
            }
            break;
        }
    }
}

// ----- Helper Functions -----

// Checks that the pattern string only includes allowed mask tokens (0, 9, #, L, ?, A, a, &, C)
// along with common literal characters (parentheses, spaces, dashes, plus, etc.).
function isValidKendoPattern(pattern) {
    const regex = /^[0-9#L\?Aa&C\(\)\-\+\s]+$/;
    return regex.test(pattern);
}

// Validates that defaultValue matches the given pattern mask character–by–character.
// (This is a basic implementation based on the provided mask guide.)
function validateDefaultAgainstPattern(pattern, defaultValue) {
    if (pattern.length !== defaultValue.length) return false;
    for (let i = 0; i < pattern.length; i++) {
        const maskChar = pattern[i];
        const defChar = defaultValue[i];
        switch (maskChar) {
            case "0":
                if (!/\d/.test(defChar)) return false;
                break;
            case "9":
                if (!(/\d/.test(defChar) || defChar === " ")) return false;
                break;
            case "#":
                if (!(/\d/.test(defChar) || defChar === " " || defChar === "+" || defChar === "-"))
                    return false;
                break;
            case "L":
                if (!/[A-Za-z]/.test(defChar)) return false;
                break;
            case "?":
                if (!(/[A-Za-z]/.test(defChar) || defChar === " ")) return false;
                break;
            case "A":
                if (!/[A-Za-z0-9]/.test(defChar)) return false;
                break;
            case "a":
                if (!(/[A-Za-z0-9]/.test(defChar) || defChar === " ")) return false;
                break;
            case "&":
                if (defChar === " ") return false;
                break;
            case "C":
                // Any character is allowed.
                break;
            default:
                // For literal characters they must match exactly.
                if (maskChar !== defChar) return false;
        }
    }
    return true;
}

// Checks that a default date string follows the required format:
// four components (separated by '_'), where the first is "Today",
// the second is '+' or '-', the third is one of "Day", "Week", or "Year",
// and the fourth is a number (offset).
function isValidDefaultDate(defaultDate) {
    const parts = defaultDate.split("_");
    if (parts.length !== 4) return false;
    if (parts[0] !== "Today") return false;
    if (parts[1] !== "+" && parts[1] !== "-") return false;
    if (!["Day", "Week", "Year"].includes(parts[2])) return false;
    if (isNaN(Number(parts[3]))) return false;
    return true;
}

// Validates time format as "HH:MM [AM|PM]" (e.g., "12:00 AM" or "1:05 PM").
function isValidTimeFormat(timeStr) {
    const regex = /^(0?[1-9]|1[0-2]):[0-5][0-9]\s?(AM|PM)$/i;
    return regex.test(timeStr);
}

// Basic email validation.
function isValidEmail(email) {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
}

// Basic URL validation.
function isValidURL(url) {
    const regex = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
    return regex.test(url);
}

// Recursively validate each node in the treeDropdown array.
function validateTreeDropdownNode(node) {
    if (typeof node !== "object" || node === null) {
        throw new Error("Each treeDropdown node must be an object.");
    }
    if (!node.hasOwnProperty("text") || typeof node.text !== "string" || node.text.trim() === "") {
        throw new Error("Each treeDropdown node must have a non-empty string 'text'.");
    }
    if (node.hasOwnProperty("root") && typeof node.root !== "boolean") {
        throw new Error("TreeDropdown node 'root' property must be a boolean if present.");
    }
    // Recursively validate child nodes
    node.items.forEach(child => {
        validateTreeDropdownNode(child);
    });
  }